import { DatePipe, LowerCasePipe } from '@angular/common';
import { Component, computed, effect, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TalkingPointAiComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/talking-point-ai/talking-point-ai.component';
import { IKpiCard } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { AnalyticsDetailsTableComponent } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { LineGraphComponent } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.component';
import { ILineGraphInputData } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.interface';
import { EAnalyticsSentiment, ETalkingPointAiSource } from '@enums/campaign-analytics.enum';
import { EDeliverableType } from '@enums/campaign.enum';
import { ITalkingPointAiAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { EmbeddedVideoPlayerComponent } from '@shared/components/embedded-video-player/embedded-video-player.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SegmentedControlComponent } from '@shared/components/segmented-control/segmented-control.component';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { StreamDetailsDeliverableBaseDirective } from '../../stream-details-deliverable-base.directive';
import {
  ITalkingPointAiStreamAnalyticsTableData,
  TTalkingPointAiSourceFilter,
} from './talking-point-ai-stream-analytics.interface';
import {
  buildTalkingPointAiSourceFilterOptions,
  filterTalkingPointAiRowsBySource,
} from './talking-point-ai-stream-analytics.util';

@Component({
  imports: [
    AnalyticsDetailsTableComponent,
    DatePipe,
    EmbeddedVideoPlayerComponent,
    DotsSpinnerComponent,
    IconComponent,
    LineGraphComponent,
    LowerCasePipe,
    MatButtonModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    SegmentedControlComponent,
    TalkingPointAiComponent,
    TranslateModule,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-talking-point-ai-stream-analytics',
  styleUrl: './talking-point-ai-stream-analytics.component.scss',
  templateUrl: './talking-point-ai-stream-analytics.component.html',
})
export class TalkingPointAiStreamAnalyticsComponent extends StreamDetailsDeliverableBaseDirective<
  ITalkingPointAiStreamAnalyticsTableData,
  ITalkingPointAiAnalytics
> {
  public override readonly kpiCardData: IKpiCard = {
    icon: 'view.svg',
    id: 'talkingPointAi-count',
    title: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.talkingPointAi.totalCount'),
    value: 0,
  };

  public override readonly displayedColumns: string[] = ['timestamp', 'videoTime', 'source', 'message', 'actions'];
  public override readonly dataSource: MatTableDataSource<ITalkingPointAiStreamAnalyticsTableData> =
    new MatTableDataSource();

  public override readonly deliverableType = EDeliverableType.talkingPointAi;

  public readonly sourceFilter = signal<TTalkingPointAiSourceFilter>('all');
  public readonly ETalkingPointAiSource = ETalkingPointAiSource;

  protected override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.talkingPointAis?.find((tp) => tp.deliverableId === deliverableId);
    }

    return null;
  });

  // Null until the rows are built: findings can arrive before the timeline, and an empty list would show 0 counts.
  private readonly allTableData = signal<ITalkingPointAiStreamAnalyticsTableData[] | null>(null);
  private readonly allDotGraphData = signal<ILineGraphInputData[]>([]);
  private readonly topicKey = signal<string>('');

  public readonly sourceFilterOptions = computed(() =>
    buildTalkingPointAiSourceFilterOptions(this.allTableData() ?? []),
  );
  public readonly sourceFilterLoading = computed(() => !this.findingsLoaded() || this.allTableData() === null);

  constructor() {
    super();

    effect(() => {
      if (this.executionAnalyticsWithFindings()) {
        this.setCounts();
        this.setStatus();
        this.setStreamData();
        this.setKeywordList();
      }
    });

    effect(() => {
      this.manualFindingStatusStore?.serverRemovedIds();

      if (this.executionAnalyticsWithFindings() && this.timelineData()) {
        this.prepareChartAndTableData();
      }
    });

    // Findings can be filtered by source without recomputing the underlying data,
    // so keep the full data in signals and derive the visible table/graph/keyword-jump state here.
    effect(() => {
      const sourceFilter = this.sourceFilter();
      const tableData = this.allTableData() ?? [];
      const dotGraphData = this.allDotGraphData();
      const topicKey = this.topicKey();

      const visibleTableData = filterTalkingPointAiRowsBySource(tableData, sourceFilter);
      const visibleIds = new Set(visibleTableData.map((row) => row.id));

      this.dataSource.data = visibleTableData;
      this.dataCount.set(visibleTableData.length);
      this.dotGraphData.set(
        [...dotGraphData].filter((dot) => visibleIds.has(dot.id)).sort((a, b) => a.xAxisValue - b.xAxisValue),
      );
      this.setKeywordTableRowIds(
        topicKey && visibleTableData.length > 0 ? { [topicKey]: visibleTableData.map((row) => row.id) } : {},
      );
    });
  }

  protected override prepareChartAndTableData(): void {
    const tableData: ITalkingPointAiStreamAnalyticsTableData[] = [];
    const dotGraphData: ILineGraphInputData[] = [];
    const currentTalkingPointAiAnalytics = this.currentDeliverable();

    const timelineData = this.timelineData() || [];
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    // Transcript findings arrive first and chat findings are appended at the end, so re-sort by
    // timestamp ascending here, before the data is split into the table/graph/keyword structures.
    const findings = (currentTalkingPointAiAnalytics?.findings || [])
      .filter((f) => !removedIds.has(f.id))
      .sort(
        (a, b) =>
          this.dateUtils.parseDateTimeFromApi(a.timestamp).getTime() -
          this.dateUtils.parseDateTimeFromApi(b.timestamp).getTime(),
      );

    const { sortedDictionary } = this.graphUtilities.processTimelineData(
      timelineData,
      this.stream()?.executionTime,
      this.stream()?.end,
    );

    findings.forEach((finding) => {
      const { id } = finding;
      const entryTimestamp = this.dateUtils.parseDateTimeFromApi(finding.timestamp).getTime();
      const interpolatedViewCount = this.graphUtilities.getInterpolatedViewCount(sortedDictionary, entryTimestamp);

      dotGraphData.push({
        id,
        status: currentTalkingPointAiAnalytics?.status ?? finding.status,
        xAxisValue: entryTimestamp,
        yAxisValue: Math.round(interpolatedViewCount),
      });

      tableData.push({
        findingParentId: finding.parentId,
        id,
        message: finding.text ?? '',
        reason: finding.reason,
        sentiment: finding.sentiment,
        source: finding.source,
        timestamp: finding.timestamp,
        videoTimeLabel: AnalyticsDetailsUtilitiesService.formatVideoTime(finding.videoTime),
        videoTimeSeconds: finding.videoTime ?? 0,
      });
    });

    this.topicKey.set(
      (currentTalkingPointAiAnalytics?.target ?? this.deliverableForm().get('text')?.getRawValue() ?? '') as string,
    );
    this.allTableData.set(tableData);
    this.allDotGraphData.set(dotGraphData);
  }

  protected override setCounts(): void {
    const talkingPointAi = this.currentDeliverable();

    const removedIds = this.manualFindingStatusStore?.allRemovedIds() ?? new Set<string>();
    const activeCount = (talkingPointAi?.findings ?? []).filter((f) => !removedIds.has(f.id)).length;

    this.kpiCardData.value = activeCount;

    AnalyticsDetailsUtilitiesService.mapTextDescriptionDeliverableAnalyticsToForm(
      this.deliverableForm(),
      talkingPointAi,
      EDeliverableType.talkingPointAi,
    );
  }

  protected override setKeywordList(): void {
    const tp = this.currentDeliverable();
    const topic = (tp?.target ?? this.deliverableForm().get('text')?.getRawValue() ?? '') as string;

    this.keywords = topic ? [topic] : [];
  }

  public getSentimentIcon(sentiment: EAnalyticsSentiment): string {
    return AnalyticsDetailsUtilitiesService.getSentimentIcon(sentiment);
  }

  public getSentimentIconColor(sentiment: EAnalyticsSentiment): string {
    return AnalyticsDetailsUtilitiesService.getSentimentIconColor(sentiment);
  }
}
